import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import MaskedTextField from '@/common/components/MaskedTextField'
import Modal from '@/common/components/Modal'
import SelectController from '@/common/components/SelectController'
import TextArea from '@/common/components/TextArea'
import TextField from '@/common/components/TextField'
import { cnpjCpfMask } from '@/common/constants/masks.constant'
import { FormTransformers } from '@/common/utils/formTransformers'
import { options as durationInMonthsOptions } from '@/features/privateCompanyLeadsFunnel/constants/durationInMonthsOptions.constant'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { BadgePlus } from 'lucide-react'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import useCreatePublicCompanyLead from '../hooks/useCreatePublicCompanyLead'
import { PublicClientsFunnelValidationSchema, createSchema } from '../schemas/create.schema'
import Button from '../../../common/components/Button'

const CreateModal = () => {
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [serverError, setServerError] = useState(false)

  const {
    control,
    register,
    handleSubmit,
    setError,
    reset: resetForm,
    formState: { errors },
  } = useForm<PublicClientsFunnelValidationSchema>({
    resolver: zodResolver(createSchema),
  })

  const onSubmit: SubmitHandler<PublicClientsFunnelValidationSchema> = (data) => mutate(data)

  const onSuccess = () => {
    resetForm()
    setServerError(false)
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLeads'] })
    setIsOpen(false)
  }

  const { mutate, isPending: useCreatePublicCompanyLeadIsPending } = useCreatePublicCompanyLead(
    onSuccess,
    FormTransformers.errorsTransformer<PublicClientsFunnelValidationSchema>({ setError }),
  )

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      dataTestid="submit_button"
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button name="create_new_public_company" className="bg-transparent hover:bg-transparent p-0" tooltipPlace='right'
          operationKlassToCheckPermission={"Admin::Funnel::Public::Leads::Create"}><BadgePlus color="#7CC4F8" strokeWidth={1.5} /></Button>
      }
      title="Cadastrando oportunidade"
      footer="Cadastrar"
      body={
        <form className="flex flex-col gap-4">
          {serverError && (
            <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>
          )}
          <TextField
            label="Nome da empresa"
            markAsRequired
            errorMessage={errors?.display_name?.message || undefined}
            {...register('display_name')}
            disabled={useCreatePublicCompanyLeadIsPending}
          />
          <MaskedTextField
            label="CNPJ/CPF"
            markAsRequired
            mask={cnpjCpfMask}
            errorMessage={errors?.general_information?.cnpj?.message || undefined}
            {...register('general_information.cnpj')}
            disabled={useCreatePublicCompanyLeadIsPending}
          />
          <div className="flex items-start gap-x-2">
            <TextField
              label="Edital"
              markAsRequired
              className="w-1/2"
              errorMessage={errors.bidding_information?.notice_number?.message || undefined}
              {...register('bidding_information.notice_number')}
              disabled={useCreatePublicCompanyLeadIsPending}
            />
            <MaskedTextField
              label="Data prevista para licitação"
              markAsRequired
              mask="00/00/0000"
              className="w-1/2"
              errorMessage={errors.bidding_information?.contest_date?.message || undefined}
              {...register('bidding_information.contest_date')}
              disabled={useCreatePublicCompanyLeadIsPending}
            />
          </div>
          <div className="flex items-start gap-x-2">
            <CurrencyTextField
              label="Valor global do contrato"
              markAsRequired
              maxValue={9_999_999_999.99}
              className="w-1/2"
              errorMessage={errors.contract?.global_value?.message || undefined}
              {...register('contract.global_value')}
              disabled={useCreatePublicCompanyLeadIsPending}
            />
            <SelectController
              control={control}
              name="contract.duration_in_months"
              label="Vigência do contrato"
              className="w-1/2"
              errorMessage={errors?.contract?.duration_in_months?.message || undefined}
              options={durationInMonthsOptions}
              isDisabled={useCreatePublicCompanyLeadIsPending}
              markAsRequired
            />
          </div>
          <TextArea
            label="Suas observações"
            markAsRequired
            maxLength={1500}
            errorMessage={errors?.observations?.message}
            {...register('observations')}
          />
        </form>
      }
    />
  )
}

export default CreateModal
